import React, { useState } from 'react';
import { bookingBrandInfoChromeStyles, teboxDesignSystem } from '../../styles/tebox-booking';
import type { BookingBrandColors } from '../../utils/booking-brand-colors';
import { buildBookingSurfaceStyles, headingTextStyle } from '../../utils/booking-brand-colors';
import type { ShopCatalogPass, ShopCatalogMembership } from '../../services/shop';
import { formatMoneyFromCents } from '../../utils/format-money';
import GiftCardIcon from '../Icons/GiftCardIcon';
import { ICON_STROKE } from '../../constants/icon-stroke';
import { PassesIcon } from '../Icons/PassesIcon';
import { Users } from 'lucide-react';

type Surface = ReturnType<typeof buildBookingSurfaceStyles>;

const sp = teboxDesignSystem.spacing;

/** Hero price numerals on shop product cards (gift card amount, pass price). */
function shopCardHeroPriceStyle(brand: BookingBrandColors): React.CSSProperties {
  return headingTextStyle({
    headingTextBold: brand.headingTextBold,
    headingTextItalic: brand.headingTextItalic,
  });
}

/** Product / summary cards on shop — white fill, brand border only (no tinted chrome). */
export function shopCardStyle(
  surface: Surface,
  brand: BookingBrandColors,
): React.CSSProperties {
  return {
    ...surface.card,
    backgroundColor: '#FFFFFF',
    border: `1px solid ${brand.borderColor}`,
  };
}

export function ShopSectionHeader({
  icon,
  title,
  surface,
}: {
  icon: React.ReactNode;
  title: string;
  surface: Surface;
}) {
  return (
    <div
      className="flex items-center gap-2"
      style={{ marginBottom: sp.md }}
    >
      {icon}
      <h2
        style={{
          ...surface.heading,
          fontSize: teboxDesignSystem.typography.h4.fontSize,
        }}
      >
        {title}
      </h2>
    </div>
  );
}

function iconWellStyle(brand: BookingBrandColors): React.CSSProperties {
  const chrome = bookingBrandInfoChromeStyles({
    primaryColor: brand.buttonColor || brand.primaryColor,
    buttonColor: brand.buttonColor,
  });
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 44,
    height: 44,
    borderRadius: teboxDesignSystem.borderRadius.sm,
    ...chrome,
    flexShrink: 0,
    boxSizing: 'border-box',
  };
}

function ProductCardHeader({
  brand,
  icon,
  children,
}: {
  brand: BookingBrandColors;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: sp.sm,
      }}
    >
      <div style={{ minWidth: 0, flex: 1 }}>{children}</div>
      <div style={iconWellStyle(brand)}>{icon}</div>
    </div>
  );
}

function useProductCardShell(surface: Surface, brand: BookingBrandColors) {
  const [hovered, setHovered] = useState(false);
  const cardStyle: React.CSSProperties = {
    ...shopCardStyle(surface, brand),
    display: 'flex',
    flexDirection: 'column',
    gap: sp.sm,
    padding: sp.md,
    height: '100%',
    boxSizing: 'border-box',
    boxShadow: hovered ? teboxDesignSystem.shadows.sm : 'none',
    transition: 'box-shadow 0.15s ease',
  };
  return { cardStyle, hovered, setHovered };
}

export function GiftCardPresetCard({
  amountCents,
  brandColors,
  surface,
  onAdd,
}: {
  amountCents: number;
  brandColors: BookingBrandColors;
  surface: Surface;
  onAdd: () => void;
}) {
  const { cardStyle, setHovered } = useProductCardShell(surface, brandColors);

  return (
    <article
      style={cardStyle}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <ProductCardHeader
        brand={brandColors}
        icon={<GiftCardIcon size={22} color={brandColors.textColor} strokeWidth={ICON_STROKE.standard} />}
      >
        <p style={{ ...surface.muted, margin: 0 }}>Gift card</p>
        <p
          style={{
            ...surface.body,
            margin: 0,
            marginTop: 2,
            fontSize: teboxDesignSystem.typography.h4.fontSize,
            lineHeight: teboxDesignSystem.typography.h4.lineHeight,
            ...shopCardHeroPriceStyle(brandColors),
          }}
        >
          {formatMoneyFromCents(amountCents)}
        </p>
      </ProductCardHeader>
      <p style={{ ...surface.muted, margin: 0, flex: 1 }}>
        Buy for yourself or send as a gift with a personal message.
      </p>
      <button
        type="button"
        onClick={onAdd}
        style={{
          ...surface.primaryButton,
          width: '100%',
          padding: '12px 16px',
          fontWeight: 500,
          fontSize: teboxDesignSystem.typography.body.fontSize,
        }}
      >
        Add to cart
      </button>
    </article>
  );
}

export function GiftCardCustomAmountCard({
  brandColors,
  surface,
  minCents,
  maxCents,
  value,
  onChange,
  onAdd,
}: {
  brandColors: BookingBrandColors;
  surface: Surface;
  minCents: number;
  maxCents: number;
  value: string;
  onChange: (value: string) => void;
  onAdd: () => void;
}) {
  const { cardStyle, setHovered } = useProductCardShell(surface, brandColors);

  return (
    <article
      style={cardStyle}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <ProductCardHeader
        brand={brandColors}
        icon={<GiftCardIcon size={22} color={brandColors.textColor} strokeWidth={ICON_STROKE.standard} />}
      >
        <p style={{ ...surface.body, margin: 0, fontWeight: 500 }}>Custom amount</p>
        <p style={{ ...surface.muted, margin: 0, marginTop: 2 }}>
          {formatMoneyFromCents(minCents)} – {formatMoneyFromCents(maxCents)}
        </p>
        <p style={{ ...surface.muted, margin: 0, marginTop: 4 }}>
          For yourself or as a gift.
        </p>
      </ProductCardHeader>
      <label className="flex flex-col gap-1" style={{ flex: 1 }}>
        <span style={surface.muted}>Amount</span>
        <input
          type="number"
          min={minCents / 100}
          max={maxCents / 100}
          step="0.01"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="0.00"
          style={{ ...surface.input, width: '100%', padding: '10px 12px' }}
        />
      </label>
      <button
        type="button"
        onClick={onAdd}
        style={{
          ...surface.primaryButton,
          width: '100%',
          padding: '12px 16px',
          fontWeight: 500,
        }}
      >
        Add to cart
      </button>
    </article>
  );
}

export function ShopPassCard({
  pass,
  brandColors,
  surface,
  inCart,
  onAdd,
}: {
  pass: ShopCatalogPass;
  brandColors: BookingBrandColors;
  surface: Surface;
  inCart: boolean;
  onAdd: () => void;
}) {
  const { cardStyle, setHovered } = useProductCardShell(surface, brandColors);
  const hasSavings = pass.savingsPercentage > 0 && pass.savingsAmountCents > 0;

  return (
    <article
      style={cardStyle}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <ProductCardHeader
        brand={brandColors}
        icon={<PassesIcon size={22} color={brandColors.textColor} />}
      >
        <h3
          style={{
            ...surface.body,
            margin: 0,
            fontWeight: 500,
            fontSize: teboxDesignSystem.typography.body.fontSize,
            lineHeight: 1.35,
          }}
        >
          {pass.name}
        </h3>
        {pass.totalQuantity > 0 ? (
          <p style={{ ...surface.muted, margin: 0, marginTop: 4 }}>
            {pass.totalQuantity} {pass.totalQuantity === 1 ? 'use' : 'uses'} included
          </p>
        ) : null}
      </ProductCardHeader>

      {pass.description ? (
        <p
          style={{
            ...surface.muted,
            margin: 0,
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {pass.description}
        </p>
      ) : null}

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: sp.sm,
          marginTop: 'auto',
        }}
      >
        <div>
          <p
            style={{
              ...surface.body,
              margin: 0,
              fontSize: teboxDesignSystem.typography.h4.fontSize,
              lineHeight: teboxDesignSystem.typography.h4.lineHeight,
              ...shopCardHeroPriceStyle(brandColors),
            }}
          >
            {formatMoneyFromCents(pass.passPriceCents)}
            {pass.taxRate > 0 ? (
              <span style={{ ...surface.muted, fontSize: teboxDesignSystem.typography.caption.fontSize }}>
                {' '}
                + tax
              </span>
            ) : null}
          </p>
          {hasSavings ? (
            <p style={{ ...surface.muted, margin: 0, marginTop: 4 }}>
              Save {Math.round(pass.savingsPercentage)}%
            </p>
          ) : null}
        </div>
      </div>

      <button
        type="button"
        disabled={inCart}
        onClick={onAdd}
        style={{
          ...surface.primaryButton,
          width: '100%',
          padding: '12px 16px',
          fontWeight: 500,
          opacity: inCart ? 0.55 : 1,
          cursor: inCart ? 'default' : 'pointer',
        }}
      >
        {inCart ? 'In cart' : 'Add to cart'}
      </button>
    </article>
  );
}

export function ShopGiftCardsSection({
  presetAmountsCents,
  minCents,
  maxCents,
  customValue,
  onCustomChange,
  onAddPreset,
  onAddCustom,
  brandColors,
  surface,
}: {
  presetAmountsCents: number[];
  minCents: number;
  maxCents: number;
  customValue: string;
  onCustomChange: (v: string) => void;
  onAddPreset: (cents: number) => void;
  onAddCustom: () => void;
  brandColors: BookingBrandColors;
  surface: Surface;
}) {
  return (
    <section style={{ marginBottom: sp.xl }}>
      <ShopSectionHeader
        icon={<GiftCardIcon size={22} color={brandColors.textColor} strokeWidth={ICON_STROKE.standard} />}
        title="Gift cards"
        surface={surface}
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {presetAmountsCents.map((cents) => (
          <GiftCardPresetCard
            key={cents}
            amountCents={cents}
            brandColors={brandColors}
            surface={surface}
            onAdd={() => onAddPreset(cents)}
          />
        ))}
        <GiftCardCustomAmountCard
          brandColors={brandColors}
          surface={surface}
          minCents={minCents}
          maxCents={maxCents}
          value={customValue}
          onChange={onCustomChange}
          onAdd={onAddCustom}
        />
      </div>
    </section>
  );
}

export function ShopPassesSection({
  passes,
  cartPassIds,
  brandColors,
  surface,
  onAddPass,
}: {
  passes: ShopCatalogPass[];
  cartPassIds: Set<string>;
  brandColors: BookingBrandColors;
  surface: Surface;
  onAddPass: (pass: ShopCatalogPass) => void;
}) {
  return (
    <section>
      <ShopSectionHeader
        icon={<PassesIcon size={22} color={brandColors.textColor} />}
        title="Passes"
        surface={surface}
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {passes.map((pass) => (
          <ShopPassCard
            key={pass.id}
            pass={pass}
            brandColors={brandColors}
            surface={surface}
            inCart={cartPassIds.has(pass.id)}
            onAdd={() => onAddPass(pass)}
          />
        ))}
      </div>
    </section>
  );
}

export function ShopMembershipCard({
  membership,
  brandColors,
  surface,
  onRequest,
}: {
  membership: ShopCatalogMembership;
  brandColors: BookingBrandColors;
  surface: Surface;
  onRequest: () => void;
}) {
  const { cardStyle, setHovered } = useProductCardShell(surface, brandColors);

  return (
    <article
      style={cardStyle}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <ProductCardHeader
        brand={brandColors}
        icon={<Users size={22} color={brandColors.textColor} />}
      >
        <h3
          style={{
            ...surface.body,
            margin: 0,
            fontWeight: 500,
            fontSize: teboxDesignSystem.typography.body.fontSize,
            lineHeight: 1.35,
          }}
        >
          {membership.name}
        </h3>
        {membership.priceCents != null ? (
          <p style={{ ...surface.muted, margin: 0, marginTop: 4 }}>
            From {formatMoneyFromCents(membership.priceCents)}
          </p>
        ) : null}
      </ProductCardHeader>

      {membership.description ? (
        <p
          style={{
            ...surface.muted,
            margin: 0,
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            flex: 1,
          }}
        >
          {membership.description}
        </p>
      ) : (
        <p style={{ ...surface.muted, margin: 0, flex: 1 }}>
          Submit your details and the club will contact you about membership.
        </p>
      )}

      <button
        type="button"
        onClick={onRequest}
        style={{
          ...surface.primaryButton,
          width: '100%',
          padding: '12px 16px',
          fontWeight: 500,
        }}
      >
        Request info
      </button>
    </article>
  );
}

export function ShopMembershipsSection({
  memberships,
  brandColors,
  surface,
  onRequestMembership,
}: {
  memberships: ShopCatalogMembership[];
  brandColors: BookingBrandColors;
  surface: Surface;
  onRequestMembership: (membership: ShopCatalogMembership) => void;
}) {
  return (
    <section style={{ marginTop: sp.xl }}>
      <ShopSectionHeader
        icon={<Users size={22} color={brandColors.textColor} />}
        title="Membership"
        surface={surface}
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {memberships.map((membership) => (
          <ShopMembershipCard
            key={membership.id}
            membership={membership}
            brandColors={brandColors}
            surface={surface}
            onRequest={() => onRequestMembership(membership)}
          />
        ))}
      </div>
    </section>
  );
}

export function shopCartPassIds(cart: Array<{ type: string; passProductId?: string }>): Set<string> {
  const ids = new Set<string>();
  for (const line of cart) {
    if (line.type === 'pass' && line.passProductId) ids.add(line.passProductId);
  }
  return ids;
}
